import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert
} from '@mui/material';
import { Flag as ReportIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';

interface ReportMessageModalProps {
  open: boolean;
  onClose: () => void;
  messageId: string | null;
}

const REPORT_REASONS = [
  'Spam or misleading',
  'Harassment or bullying',
  'Inappropriate content',
  'Scam or fraud',
  'Hate speech',
  'Other'
];

const ReportMessageModal: React.FC<ReportMessageModalProps> = ({
  open,
  onClose,
  messageId
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }

    const reason = selectedReason + (additionalInfo ? `: ${additionalInfo}` : '');
    
    if (reason.length < 10) {
      setError('Please provide more detail (minimum 10 characters total)');
      return;
    }

    if (!messageId) {
      setError('Invalid message');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response: any = await apiService.post('/message-actions/report', {
        messageId,
        reason,
        additionalInfo: additionalInfo || undefined
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(response.error || response.message || 'Failed to submit report');
      }
    } catch (err: any) {
      console.error('Error reporting message:', err);
      setError(err.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setAdditionalInfo('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.875rem' }}>
          <ReportIcon sx={{ fontSize: '1.875rem' }} />
          Report Message
        </Typography>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ fontSize: '1.5rem', '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
            <Typography variant="body1" sx={{ fontSize: '1.5rem' }}>
              Thank you for your report. Our support team will review it shortly.
            </Typography>
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '1.3125rem' }}>
              Help us understand what's wrong with this message. Your report will be reviewed by our support team.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, fontSize: '1.3125rem', '& .MuiAlert-message': { fontSize: '1.3125rem' } }}>
                {error}
              </Alert>
            )}

            <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: '1.3125rem' }}>
                Reason for report:
              </Typography>
              <RadioGroup
                value={selectedReason}
                onChange={(e) => {
                  setSelectedReason(e.target.value);
                  setError(null);
                }}
              >
                {REPORT_REASONS.map((reason) => (
                  <FormControlLabel
                    key={reason}
                    value={reason}
                    control={<Radio sx={{ fontSize: '1.5rem' }} />}
                    label={reason}
                    sx={{ fontSize: '1.3125rem', '& .MuiFormControlLabel-label': { fontSize: '1.3125rem' } }}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional information (optional)"
              placeholder="Provide more details about this report..."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText={`${additionalInfo.length}/500 characters`}
              sx={{
                '& .MuiInputBase-input': { fontSize: '1.5rem' },
                '& .MuiInputLabel-root': { fontSize: '1.3125rem' },
                '& .MuiFormHelperText-root': { fontSize: '1.125rem' }
              }}
            />
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={submitting} sx={{ fontSize: '1.3125rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="error"
            disabled={!selectedReason || submitting}
            sx={{ fontSize: '1.3125rem' }}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ReportMessageModal;

